import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../utils';

import { toggleVariants } from './Toggle';

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  variant: 'default',
});

function ToggleGroup({
  className,
  variant,
  children,
  label,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof toggleVariants> & {
    spacing?: number;
    label?: string;
  }) {
  return (
    <div className="flex items-center gap-3">
      {label ? (
        <span data-slot="toggle-group-label" className="text-xs font-medium">
          {label}
        </span>
      ) : null}
      <ToggleGroupPrimitive.Root
        data-slot="toggle-group"
        data-variant={variant}
        className={cn(
          'group/toggle-group flex w-fit items-center gap-2 rounded-sm',
          className,
        )}
        {...props}
      >
        <ToggleGroupContext.Provider value={{ variant }}>
          {children}
        </ToggleGroupContext.Provider>
      </ToggleGroupPrimitive.Root>
    </div>
  );
}

function ToggleGroupItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={context.variant}
      className={cn(
        toggleVariants({
          variant: context.variant,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

export { ToggleGroup, ToggleGroupItem };
